// src/components/SelectPillarForm.jsx
import React, { useState, useEffect } from 'react';

const SelectPillarForm = ({ quote, onSubmit, onCancel }) => {
  const [pillars, setPillars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [selectedPillar, setSelectedPillar] = useState('');
  const [impactLevel, setImpactLevel] = useState('moderate');
  const [justification, setJustification] = useState('');
  
  // Load pillars when component mounts
  useEffect(() => {
    const fetchPillars = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/admin/pillars');
        if (!response.ok) {
          throw new Error('Failed to fetch pillars');
        }
        const data = await response.json();
        setPillars(data);
      } catch (error) {
        console.error('Error fetching pillars:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPillars();
  }, []);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedPillar || !impactLevel || !justification) {
      alert('All fields are required');
      return;
    }
    
    onSubmit({
      pillar_id: selectedPillar,
      impact_level: impactLevel,
      justification
    });
  };
  
  if (loading) {
    return <div className="text-center py-4">Loading pillars...</div>;
  }
  
  if (error) {
    return (
      <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4">
        <p className="font-bold">Error</p>
        <p>{error}</p>
      </div>
    );
  }
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <h4 className="font-medium text-lg mb-2">Connect Quote to Democratic Pillar</h4>
        <p className="text-sm text-gray-500 mb-4">
          Select a democratic pillar that this claim threatens or impacts.
        </p>
      </div>
      
      <div className="space-y-4">
        <div>
          <label htmlFor="pillar" className="block text-sm font-medium text-gray-700 mb-1">
            Democratic Pillar
          </label>
          <select
            id="pillar"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            value={selectedPillar}
            onChange={(e) => setSelectedPillar(e.target.value)}
            required
          >
            <option value="">Select a pillar</option>
            {pillars.map((pillar) => (
              <option key={pillar.id} value={pillar.id}>
                {pillar.name}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label htmlFor="impactLevel" className="block text-sm font-medium text-gray-700 mb-1">
            Impact Level
          </label>
          <select
            id="impactLevel"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            value={impactLevel}
            onChange={(e) => setImpactLevel(e.target.value)}
            required
          >
            <option value="severe">Severe</option>
            <option value="moderate">Moderate</option>
            <option value="mild">Mild</option>
          </select>
          <p className="mt-1 text-xs text-gray-500">
            How severely does this claim threaten this democratic pillar?
          </p>
        </div>
        
        <div>
          <label htmlFor="justification" className="block text-sm font-medium text-gray-700 mb-1">
            Justification
          </label>
          <textarea
            id="justification"
            rows="4"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            placeholder="Explain how this claim impacts or threatens this democratic pillar..."
            required
          ></textarea>
        </div>
      </div>
      
      <div className="flex justify-end pt-4 space-x-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-blue-700"
        >
          Connect
        </button>
      </div>
    </form>
  );
};

export default SelectPillarForm;